import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Download, Edit, Trash2, Eye, Tag, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';

interface Unit {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const UnitsPage: React.FC = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [newUnit, setNewUnit] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  const { settings } = useSettings();

  useEffect(() => {
    fetchUnits();
  }, []);

  // Soft refresh for units
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateMasterDataCache();
      fetchUnits();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/units?limit=1000'); // Request all units
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      toast.error('Failed to fetch units');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUnit = async () => {
    try {
      const response = await apiService.post('/units', newUnit);
      toast.success('Unit added successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowAddModal(false);
      setNewUnit({ name: '', description: '', isActive: true });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchUnits();
      }, 100);
    } catch (error) {
      toast.error('Failed to add unit');
    }
  };

  const handleEditUnit = async () => {
    if (!editingUnit) return;
    try {
      const response = await apiService.put(`/units/${editingUnit._id}`, editingUnit);
      toast.success('Unit updated successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowEditModal(false);
      setEditingUnit(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchUnits();
      }, 100);
    } catch (error) {
      toast.error('Failed to update unit');
    }
  };

  const handleDeleteUnit = async (unitId: string) => {
    if (window.confirm('Are you sure you want to delete this unit?')) {
      try {
        const response = await apiService.delete(`/units/${unitId}`);
        toast.success('Unit deleted successfully');
        
        // Immediately clear master data cache and force refresh
        apiService.invalidateMasterDataCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchUnits();
        }, 100);
      } catch (error) {
        console.error('Error deleting unit:', error);
        toast.error('Failed to delete unit');
      }
    }
  };

  const openEditModal = (unit: Unit) => {
    setEditingUnit(unit);
    setShowEditModal(true);
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         unit.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || unit.isActive === (statusFilter === 'active');
    return matchesSearch && matchesStatus;
  });

  const exportUnits = async () => {
    try {
      await apiService.download('/units/export/excel', 'units.xlsx');
      toast.success('Units exported successfully');
    } catch (error) {
      console.error('Error exporting units:', error);
      toast.error('Failed to export units');
    }
  };

  const handleToggleStatus = async (unit: Unit) => {
    try {
      await apiService.patch(`/units/${unit._id}/toggle`);
      fetchUnits();
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>Units</h1>
          <p style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>Manage your product units</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateMasterDataCache(); 
              fetchUnits(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Unit
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search units..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportUnits}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      {/* Units Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="w-full">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Unit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredUnits.map((unit) => (
                <tr key={unit._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Tag className="w-5 h-5 text-[var(--color-primary)] mr-3" />
                      <div>
                        <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">{unit.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{unit.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-['Times_New_Roman']">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${unit.isActive ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'}`}>{unit.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(unit.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-['Times_New_Roman']">
                    <div className="flex items-center justify-end space-x-2">
                      <button className="p-2 text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors" title="View Details"><Eye className="w-4 h-4" /></button>
                      <button className="p-2 text-[var(--color-success)] hover:bg-[var(--color-success-light)] rounded-lg transition-colors" title="Edit Unit" onClick={() => openEditModal(unit)}><Edit className="w-4 h-4" /></button>
                      <button className="p-2 text-[var(--color-warning)] hover:bg-[var(--color-warning-light)] rounded-lg transition-colors" title="Toggle Status" onClick={() => handleToggleStatus(unit)}>{unit.isActive ? 'Deactivate' : 'Activate'}</button>
                      <button className="p-2 text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] rounded-lg transition-colors" title="Delete Unit" onClick={() => handleDeleteUnit(unit._id)}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>
        {/* Empty State */}
        {filteredUnits.length === 0 && (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No units found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first unit.'}
            </p>
          </div>
        )}
      </div>
      {/* Add Unit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Unit"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newUnit.name}
            onChange={e => setNewUnit({ ...newUnit, name: e.target.value })}
            placeholder="Enter unit name"
            required
          />
          <Input
            label="Description"
            value={newUnit.description}
            onChange={e => setNewUnit({ ...newUnit, description: e.target.value })}
            placeholder="Enter description"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newUnit.isActive ? 'active' : 'inactive'}
              onChange={e => setNewUnit({ ...newUnit, isActive: e.target.value === 'active' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddUnit}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Add Unit
          </button>
        </div>
      </Modal>
      {/* Edit Unit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Unit"
      >
        {editingUnit && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingUnit.name}
              onChange={e => setEditingUnit({ ...editingUnit, name: e.target.value })}
              placeholder="Enter unit name"
              required
            />
            <Input
              label="Description"
              value={editingUnit.description || ''}
              onChange={e => setEditingUnit({ ...editingUnit, description: e.target.value })}
              placeholder="Enter description"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingUnit.isActive ? 'active' : 'inactive'}
                onChange={e => setEditingUnit({ ...editingUnit, isActive: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowEditModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditUnit}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Update Unit
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UnitsPage; 